/**
 * ============================================================
 * ESENCIA — SELECCIÓN ESPECIAL +$70
 * V1.0
 * ============================================================
 *
 * OBJETIVO
 * ------------------------------------------------------------
 * Cada unidad de un producto que tenga el tag:
 *
 *   seleccion-especial-70
 *
 * genera automáticamente:
 *
 *   + $70 MXN
 *
 * mediante el producto auxiliar:
 *
 *   extra-por-seleccion-especial
 *
 *
 * REGLAS
 * ------------------------------------------------------------
 * - El helper NO cuenta como pieza Esencia.
 * - El helper NO pertenece a "Arma tu esencia".
 * - OC Quantity Breaks continúa controlando 5 / 10 / 15 piezas.
 * - Los regalos NO generan recargo.
 * - Una unidad especial = una unidad helper.
 * - 4 unidades especiales = 4 helpers = +$280.
 * - Si se elimina un especial, se corrige automáticamente.
 * - Si quedan 0 especiales, el helper desaparece.
 *
 * ============================================================
 */

(() => {
  'use strict';

  if (window.__ESENCIA_SPECIAL_EXTRA_LOADED__) {
    return;
  }

  window.__ESENCIA_SPECIAL_EXTRA_LOADED__ = true;

  const DEFAULT_CONFIG = {
    specialTag: 'seleccion-especial-70',
    helperHandle: 'extra-por-seleccion-especial',
    helperTag: 'esencia-extra-helper',
    extraAmount: 70
  };

  const PRIVATE_HELPER_PROPERTY = '_esencia_extra_helper';
  const PRIVATE_EXTRA_PROPERTY = '_esencia_extra_amount';

  const PRODUCT_CACHE = new Map();

  const nativeFetch = window.fetch.bind(window);

  let helperVariantId = null;
  let syncPromise = null;
  let syncTimer = null;

  /* ============================================================
     UTILIDADES
     ============================================================ */

  function getShopifyRoot() {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return root.endsWith('/')
      ? root
      : `${root}/`;
  }

  function getStoreConfig() {
    const configElement =
      document.getElementById('EsenciaStoreConfig');

    if (!configElement) {
      return {
        ...DEFAULT_CONFIG
      };
    }

    try {
      const parsed =
        JSON.parse(configElement.textContent || '{}');

      const specialSelection =
        parsed.specialSelection || {};

      return {
        specialTag:
          specialSelection.tag ||
          DEFAULT_CONFIG.specialTag,

        helperHandle:
          specialSelection.helperHandle ||
          DEFAULT_CONFIG.helperHandle,

        helperTag:
          specialSelection.helperTag ||
          DEFAULT_CONFIG.helperTag,

        extraAmount:
          Number(
            specialSelection.extra ||
            DEFAULT_CONFIG.extraAmount
          )
      };
    } catch (error) {
      console.warn(
        '[Esencia] No se pudo leer EsenciaStoreConfig.',
        error
      );

      return {
        ...DEFAULT_CONFIG
      };
    }
  }

  function normalizeString(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags
        .map(normalizeString)
        .filter(Boolean);
    }

    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map(normalizeString)
        .filter(Boolean);
    }

    return [];
  }

  function getItemHandle(item) {
    return (
      item?.handle ||
      item?.product_handle ||
      ''
    );
  }

  function getItemProperties(item) {
    return (
      item &&
      typeof item.properties === 'object' &&
      item.properties !== null
        ? item.properties
        : {}
    );
  }

  function propertyIsTrue(value) {
    return (
      value === true ||
      value === 'true' ||
      value === 1 ||
      value === '1'
    );
  }

  function isGiftItem(item) {
    const properties =
      getItemProperties(item);

    if (
      propertyIsTrue(
        properties._esencia_gift
      )
    ) {
      return true;
    }

    const title =
      normalizeString(
        item?.product_title ||
        item?.title
      );

    return (
      title.includes('regalo esencia') ||
      title.includes('regalo-esencia')
    );
  }

  function isHelperItem(
    item,
    config
  ) {
    const properties =
      getItemProperties(item);

    if (
      propertyIsTrue(
        properties[
          PRIVATE_HELPER_PROPERTY
        ]
      )
    ) {
      return true;
    }

    const handle =
      normalizeString(
        getItemHandle(item)
      );

    return (
      handle ===
      normalizeString(
        config.helperHandle
      )
    );
  }

  /* ============================================================
     SHOPIFY AJAX
     ============================================================ */

  async function fetchCart() {
    const response =
      await nativeFetch(
        `${getShopifyRoot()}cart.js`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        `No se pudo leer el carrito (${response.status}).`
      );
    }

    return response.json();
  }

  async function fetchProduct(
    handle
  ) {
    if (!handle) {
      return null;
    }

    if (
      PRODUCT_CACHE.has(handle)
    ) {
      return PRODUCT_CACHE.get(
        handle
      );
    }

    const promise =
      nativeFetch(
        `${getShopifyRoot()}products/${encodeURIComponent(
          handle
        )}.js`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Producto ${handle}: ${response.status}`
            );
          }

          return response.json();
        })
        .catch((error) => {
          PRODUCT_CACHE.delete(
            handle
          );

          console.warn(
            `[Esencia] No se pudieron leer los tags de ${handle}.`,
            error
          );

          return null;
        });

    PRODUCT_CACHE.set(
      handle,
      promise
    );

    return promise;
  }

  async function getHelperVariantId(
    config
  ) {
    if (helperVariantId) {
      return helperVariantId;
    }

    const helperProduct =
      await fetchProduct(
        config.helperHandle
      );

    if (
      !helperProduct ||
      !Array.isArray(
        helperProduct.variants
      ) ||
      !helperProduct.variants.length
    ) {
      throw new Error(
        'No se encontró una variante para el producto auxiliar de Selección especial.'
      );
    }

    const availableVariant =
      helperProduct.variants.find(
        (variant) =>
          variant.available !== false
      ) ||
      helperProduct.variants[0];

    helperVariantId =
      availableVariant.id;

    return helperVariantId;
  }

  /* ============================================================
     DETECCIÓN DE PRODUCTOS ESPECIALES
     ============================================================ */

  async function productHasSpecialTag(
    item,
    config
  ) {
    if (
      !item ||
      isGiftItem(item) ||
      isHelperItem(
        item,
        config
      )
    ) {
      return false;
    }

    /*
     * Compatibilidad:
     * si alguno de nuestros componentes ya agregó
     * _esencia_special_extra = 70,
     * no necesitamos volver a consultar producto.
     */

    const properties =
      getItemProperties(item);

    const storedExtra =
      Number(
        properties
          ._esencia_special_extra ||
        0
      );

    if (
      storedExtra ===
      Number(config.extraAmount)
    ) {
      return true;
    }

    const handle =
      getItemHandle(item);

    if (!handle) {
      return false;
    }

    const product =
      await fetchProduct(handle);

    if (!product) {
      return false;
    }

    const tags =
      normalizeTags(
        product.tags
      );

    return tags.includes(
      normalizeString(
        config.specialTag
      )
    );
  }

  async function countSpecialUnits(
    cart,
    config
  ) {
    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return 0;
    }

    const results =
      await Promise.all(
        cart.items.map(
          async (item) => {
            const special =
              await productHasSpecialTag(
                item,
                config
              );

            if (!special) {
              return 0;
            }

            return Math.max(
              0,
              Number(
                item.quantity || 0
              )
            );
          }
        )
      );

    return results.reduce(
      (total, quantity) =>
        total + quantity,
      0
    );
  }

  /* ============================================================
     HELPER
     ============================================================ */

  function getHelperItems(
    cart,
    config
  ) {
    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return [];
    }

    return cart.items.filter(
      (item) =>
        isHelperItem(
          item,
          config
        )
    );
  }

  function getHelperQuantity(
    helperItems
  ) {
    return helperItems.reduce(
      (total, item) =>
        total +
        Math.max(
          0,
          Number(
            item.quantity || 0
          )
        ),
      0
    );
  }

  function helperHasCorrectProperties(
    item
  ) {
    const properties =
      getItemProperties(item);

    return propertyIsTrue(
      properties[
        PRIVATE_HELPER_PROPERTY
      ]
    );
  }

  async function removeHelperItems(
    helperItems
  ) {
    if (
      !Array.isArray(helperItems) ||
      !helperItems.length
    ) {
      return;
    }

    const updates = {};

    helperItems.forEach(
      (item) => {
        if (item.key) {
          updates[item.key] = 0;
        }
      }
    );

    if (
      !Object.keys(updates)
        .length
    ) {
      return;
    }

    const response =
      await nativeFetch(
        `${getShopifyRoot()}cart/update.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },
          body: JSON.stringify({
            updates
          })
        }
      );

    if (!response.ok) {
      throw new Error(
        `No se pudo eliminar el helper (${response.status}).`
      );
    }

    await response.json();
  }

  async function addHelper(
    quantity,
    config
  ) {
    const variantId =
      await getHelperVariantId(
        config
      );

    const response =
      await nativeFetch(
        `${getShopifyRoot()}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity,
                properties: {
                  [PRIVATE_HELPER_PROPERTY]:
                    'true',

                  [PRIVATE_EXTRA_PROPERTY]:
                    String(
                      config.extraAmount
                    )
                }
              }
            ]
          })
        }
      );

    if (!response.ok) {
      let message =
        'No se pudo agregar el cargo de Selección especial.';

      try {
        const data =
          await response.json();

        if (
          data &&
          data.description
        ) {
          message =
            data.description;
        }
      } catch (error) {}

      throw new Error(
        message
      );
    }

    return response.json();
  }

  async function changeHelperQuantity(
    helperItem,
    quantity
  ) {
    if (
      !helperItem ||
      !helperItem.key
    ) {
      return false;
    }

    const response =
      await nativeFetch(
        `${getShopifyRoot()}cart/change.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },
          body: JSON.stringify({
            id: helperItem.key,
            quantity
          })
        }
      );

    if (!response.ok) {
      throw new Error(
        `No se pudo actualizar Selección especial (${response.status}).`
      );
    }

    await response.json();

    return true;
  }

  /* ============================================================
     EVENTOS
     ============================================================ */

  function dispatchUpdatedEvent(
    detail
  ) {
    const eventDetail = {
      source:
        'esencia-special-extra',

      ...detail
    };

    document.dispatchEvent(
      new CustomEvent(
        'esencia:special-extra-updated',
        {
          detail:
            eventDetail
        }
      )
    );

    document.dispatchEvent(
      new CustomEvent(
        'esencia:cart-changed',
        {
          detail:
            eventDetail
        }
      )
    );
  }

  /* ============================================================
     SINCRONIZACIÓN
     ============================================================ */

  async function runSync() {
    const config =
      getStoreConfig();

    const cart =
      await fetchCart();

    const specialQuantity =
      await countSpecialUnits(
        cart,
        config
      );

    let helperItems =
      getHelperItems(
        cart,
        config
      );

    const helperQuantity =
      getHelperQuantity(
        helperItems
      );

    /*
     * 0 especiales:
     * el helper no debe existir.
     */

    if (
      specialQuantity === 0
    ) {
      if (
        helperItems.length
      ) {
        await removeHelperItems(
          helperItems
        );
      }

      dispatchUpdatedEvent({
        specialQuantity: 0,
        helperQuantity: 0,
        extraAmount:
          config.extraAmount,
        extraTotal: 0
      });

      return;
    }

    /*
     * Si existen helpers duplicados,
     * o uno fue agregado manualmente
     * sin nuestras propiedades privadas,
     * limpiamos y reconstruimos.
     */

    const helperNeedsRebuild =
      helperItems.length > 1 ||
      (
        helperItems.length === 1 &&
        !helperHasCorrectProperties(
          helperItems[0]
        )
      );

    if (
      helperNeedsRebuild
    ) {
      await removeHelperItems(
        helperItems
      );

      await addHelper(
        specialQuantity,
        config
      );

      dispatchUpdatedEvent({
        specialQuantity,
        helperQuantity:
          specialQuantity,
        extraAmount:
          config.extraAmount,
        extraTotal:
          specialQuantity *
          config.extraAmount
      });

      return;
    }

    /*
     * No existe helper:
     * lo agregamos.
     */

    if (
      helperItems.length === 0
    ) {
      await addHelper(
        specialQuantity,
        config
      );

      dispatchUpdatedEvent({
        specialQuantity,
        helperQuantity:
          specialQuantity,
        extraAmount:
          config.extraAmount,
        extraTotal:
          specialQuantity *
          config.extraAmount
      });

      return;
    }

    /*
     * Existe uno correctamente.
     * Solo cambiamos cantidad
     * si hace falta.
     */

    if (
      helperQuantity !==
      specialQuantity
    ) {
      await changeHelperQuantity(
        helperItems[0],
        specialQuantity
      );
    }

    dispatchUpdatedEvent({
      specialQuantity,
      helperQuantity:
        specialQuantity,
      extraAmount:
        config.extraAmount,
      extraTotal:
        specialQuantity *
        config.extraAmount
    });
  }

  function sync() {
    if (syncPromise) {
      return syncPromise;
    }

    syncPromise =
      runSync()
        .catch((error) => {
          console.error(
            '[Esencia] Error sincronizando Selección especial:',
            error
          );
        })
        .finally(() => {
          syncPromise = null;
        });

    return syncPromise;
  }

  function scheduleSync(
    delay = 120
  ) {
    if (syncTimer) {
      window.clearTimeout(
        syncTimer
      );
    }

    syncTimer =
      window.setTimeout(
        () => {
          syncTimer = null;
          sync();
        },
        delay
      );
  }

  /* ============================================================
     DETECTAR CAMBIOS DEL CARRITO
     ============================================================ */

  function isCartMutation(
    input,
    init = {}
  ) {
    let url = '';

    if (
      typeof input === 'string'
    ) {
      url = input;
    } else if (
      input &&
      typeof input.url === 'string'
    ) {
      url = input.url;
    }

    const method =
      normalizeString(
        init.method ||
        (
          input &&
          input.method
        ) ||
        'get'
      );

    if (
      method === 'get'
    ) {
      return false;
    }

    return (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
  }

  window.fetch =
    async function esenciaFetchInterceptor(
      ...args
    ) {
      const response =
        await nativeFetch(
          ...args
        );

      try {
        if (
          isCartMutation(
            args[0],
            args[1]
          )
        ) {
          scheduleSync(100);
        }
      } catch (error) {}

      return response;
    };

  /*
   * Eventos propios que ya usa Esencia.
   */

  document.addEventListener(
    'esencia:cart-changed',
    (event) => {
      if (
        event?.detail?.source ===
        'esencia-special-extra'
      ) {
        return;
      }

      scheduleSync(80);
    }
  );

  /*
   * Compatibilidad con eventos
   * utilizados por algunos temas/apps.
   */

  document.addEventListener(
    'cart:updated',
    () => {
      scheduleSync(80);
    }
  );

  document.addEventListener(
    'cart:update',
    () => {
      scheduleSync(80);
    }
  );

  window.addEventListener(
    'pageshow',
    () => {
      scheduleSync(200);
    }
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleSync(250);
      },
      {
        once: true
      }
    );
  } else {
    scheduleSync(250);
  }

  /* ============================================================
     API PÚBLICA
     ============================================================ */

  window.EsenciaSpecialExtra = {
    sync,

    getConfig:
      getStoreConfig,

    clearProductCache() {
      PRODUCT_CACHE.clear();
    }
  };
})();